import { useCallback, useMemo } from 'react';
import { Big } from 'big.js';
import { bigIntToBig, bigToString, stringToBig } from '@/utils/bigUtils';
import type { TokenDetail } from '@/utils/tokenNumberUtils';

interface UseMarginAmountSliderInput {
  marginAmountInput: string;
  setMarginAmountInput: (value: string) => void;
  maxAmount: { value: bigint; decimals: number } | undefined;
  token: TokenDetail | undefined;
}

/**
 * Two-way sync between the token-amount input and a 0–100% slider. The input is the source of truth;
 * `sliderPercent` is derived. Slider → input conversion uses `Big.roundDown` so it never exceeds balance.
 */
export function useMarginAmountSlider({
  marginAmountInput,
  setMarginAmountInput,
  maxAmount,
  token,
}: UseMarginAmountSliderInput) {
  /** Shared dp + max-as-Big — both handlers need them, and so does the slider position derivation. */
  const ctx = useMemo(() => {
    if (!maxAmount || !token) return null;
    return {
      dp: Math.min(token.display_precision ?? 2, maxAmount.decimals),
      maxBig: bigIntToBig(maxAmount.value, maxAmount.decimals),
    };
  }, [maxAmount, token]);

  const sliderPercent = useMemo(() => {
    if (!ctx || ctx.maxBig.eq(0) || !marginAmountInput.trim()) return 0;
    try {
      const pct = stringToBig(marginAmountInput).div(ctx.maxBig).times(100).toNumber();
      return Math.min(100, Math.max(0, Math.round(pct)));
    } catch {
      return 0;
    }
  }, [marginAmountInput, ctx]);

  const handleSliderChange = useCallback(
    (newPct: number) => {
      if (!ctx) return;
      if (newPct <= 0) return setMarginAmountInput('');
      setMarginAmountInput(bigToString(ctx.maxBig.times(newPct).div(100), ctx.dp, Big.roundDown));
    },
    [ctx, setMarginAmountInput]
  );

  const handleSetMax = useCallback(() => {
    if (!ctx) return;
    setMarginAmountInput(bigToString(ctx.maxBig, ctx.dp, Big.roundDown));
  }, [ctx, setMarginAmountInput]);

  return { sliderPercent, handleSliderChange, handleSetMax };
}
